import { throttle } from "lodash"

/**
 * Generates the page outline navigation UI, and handles interactivity for it.
 */
class PageOutline {
  constructor(outlineSelector, contentSelector) {
    this.baseElement = document.querySelector(outlineSelector)
    this.contentElement = document.querySelector(contentSelector)
    this.menuElement = this.baseElement.querySelector(`${outlineSelector}__nav-container`)
    this.outlineElement = this.baseElement.querySelector(`${outlineSelector}__nav`)
    this.toggleElement = this.baseElement.querySelector(`${outlineSelector}__toggle`)

    this.desktopBreakpointQuery = window.matchMedia("(min-width: 940px)")

    const headings = this.parseHeadings()
    this.renderOutline(headings)
    this.bindHeadingObserver(headings)
    this.bindMobileMenuEvents()
    this.recalculateActiveHeading()
  }

  /**
   * Binds event handler to update the active state of the page outline
   * as the user navigates through the document.
   */
  bindHeadingObserver(headings) {
    // Set initial scroll position
    this.activeHeadingIndex = 0

    // Transform heading tree into flat mapping of IDs to visibility booleans (false by default)
    this.headingList = headings.reduce((previous, next) => {
      const children = next.children.reduce((previousChild, nextChild) => [...previousChild, nextChild.id], [])
      return [...previous, next.id, ...children]
    }, [])

    window.addEventListener(
      "scroll",
      throttle(() => {
        this.recalculateActiveHeading()
      }),
      { passive: true }
    )
  }

  /**
   * Binds event handlers for menu toggle functionality, and
   * sets initial state of elements based on viewport.
   */
  bindMobileMenuEvents() {
    this.toggleElement.addEventListener("click", () => {
      this.toggleMobileMenu()
    })

    this.baseElement.addEventListener("click", event => {
      if (event.target.localName === "a" || event.target.id === "page-outline-nav") {
        setTimeout(() => {
          this.recalculateActiveHeading()

          if (this.isMobileViewport()) {
            this.toggleMobileMenu()
          }
        }, 20)
      }
      return true
    })

    this.desktopBreakpointQuery.addEventListener("change", () => {
      this.toggleMobileMenu(false)
    })

    // Force closure of menu once to ensure aria labels are in correct state on load
    this.toggleMobileMenu(false)
  }

  /**
   * Checks viewport size. Mobile is considered anything below `lg` breakpoint.
   */
  isMobileViewport() {
    return !this.desktopBreakpointQuery.matches
  }

  /**
   * Opens or closes the page outline menu on mobile by adjusting ARIA attributes
   * and disabling/enabling page-level scroll.
   *
   * @param forcedNewState Optional parameter to force the menu open or closed
   */
  toggleMobileMenu(forcedNewState = null) {
    const previousExpandedState = this.toggleElement.getAttribute("aria-expanded") === "true"
    const expandToggle = forcedNewState === null ? !previousExpandedState : forcedNewState
    let hideMenu = forcedNewState === null ? !expandToggle : !forcedNewState
    if (!this.isMobileViewport()) {
      hideMenu = false
    }

    this.toggleElement.setAttribute("aria-expanded", expandToggle ? "true" : "false")
    this.menuElement.setAttribute("aria-hidden", hideMenu ? "true" : "false")
    document.body.style.overflow = expandToggle ? "hidden" : "auto"
  }

  /**
   * Based on the position of the headings in relation to the viewport,
   * determines which heading is currently active and triggers an update.
   */
  recalculateActiveHeading() {
    // Produce list of Y offsets relative to the top of the viewport
    const headingPositions = this.headingList.map(
      heading => document.querySelector(`#${heading}`).getBoundingClientRect().y
    )

    // The first positive Y offset designates the first heading past the top of the viewport
    let firstVisibleHeadingIndex = headingPositions.findIndex(position => position > 50)
    if (firstVisibleHeadingIndex === -1) {
      firstVisibleHeadingIndex = this.headingList.length
    }
    this.activeHeadingIndex = Math.max(firstVisibleHeadingIndex - 1, 0)

    this.updateActiveHeading(this.headingList[this.activeHeadingIndex])
  }

  /**
   * Searches the content area for second- and third-level headings,
   * tags them with a unique ID for use in anchor links, and outputs
   * key information about them in a simple two-layered tree structure.
   * Third-level headings that follow a second-level heading are
   * considered children of that second-level heading.
   */
  parseHeadings() {
    let nextID = 1
    const headingElements = Array.from(this.contentElement.querySelectorAll(".typography h2, .typography h3")).filter(
      heading => heading.innerText && heading.innerText.trim() !== ""
    )
    const headings = []
    headingElements.forEach(heading => {
      heading.id = `page-outline-heading-${nextID}`
      ++nextID

      if (heading.localName === "h2" || headings.length === 0) {
        headings.push({ id: heading.id, label: heading.innerText, children: [] })
      } else {
        headings[headings.length - 1].children.push({ id: heading.id, label: heading.innerText })
      }
    })

    return headings
  }

  /**
   * Given the structure generated by {@see parseHeadings}, produces a nested navigation structure
   * and renders it in the page outline area.
   * @param headings
   */
  renderOutline(headings) {
    const topLevelList = document.createElement("ul")
    topLevelList.classList.add("page-outline__list")

    headings.forEach(heading => {
      // Only create a top level list item if heading label is not empty
      if (heading.label.trim()) {
        const headingListItemElement = document.createElement("li")
        let headingLabel = heading.label.split("|")
        headingLabel = headingLabel.length > 1 ? headingLabel[1] : heading.label

        headingListItemElement.classList.add("page-outline__item")
        headingListItemElement.innerHTML = `
          <a id="${heading.id}-link" class="page-outline__link" href="${window.location.pathname}#${heading.id}">${headingLabel}</a>
        `

        if (heading.children.length) {
          const childList = document.createElement("ul")
          childList.classList.add("page-outline__list")
          heading.children.forEach(subheading => {
            // Only create a child list item if subheading label is not empty
            if (subheading.label.trim()) {
              const subheadingListItemElement = document.createElement("li")
              subheadingListItemElement.classList.add("page-outline__item")
              subheadingListItemElement.innerHTML = `
                <a id="${subheading.id}-link" class="page-outline__link" href="${window.location.pathname}#${subheading.id}">${subheading.label}</a>
              `
              childList.appendChild(subheadingListItemElement)
            }
          })
          // Only create the child list if it has children
          if (childList.children.length > 0) {
            headingListItemElement.appendChild(childList)
          }
        }

        topLevelList.appendChild(headingListItemElement)
      }
    })

    this.outlineElement.appendChild(topLevelList)
  }

  /**
   * Replaces the previously active heading link with a new one, also
   * marking its parent active if nested.
   */
  updateActiveHeading(newlyActiveHeadingID) {
    // Clear active class from all headings
    const previouslyActiveHeadings = this.outlineElement.querySelectorAll(".page-outline__link--active")
    previouslyActiveHeadings.forEach(heading => heading.classList.remove("page-outline__link--active"))

    // Apply active class to new heading
    const newlyActiveHeading = this.outlineElement.querySelector(`#${newlyActiveHeadingID}-link`)
    newlyActiveHeading.classList.add("page-outline__link--active")

    // Traverse up and mark parent heading as active if we're acting on a child
    const parentHeading = newlyActiveHeading.parentElement.parentElement.closest("li")
    if (parentHeading) {
      parentHeading.querySelector("a").classList.add("page-outline__link--active")
    }
  }
}

export default function init(outlineSelector = ".page-outline", contentSelector = ".content") {
  if (document.querySelector(outlineSelector)) {
    new PageOutline(outlineSelector, contentSelector)
  }
}
